import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  listRoles,
  createRole,
  updateRole,
  deactivateRole,
  reactivateRole,
} from "../../../../utils/services/control/rbacAdminService";
import StatusChip from "../common/StatusChip";
import ConfirmDialog from "../common/ConfirmDialog";
import CatalogFormDialog from "./CatalogFormDialog";
import useSnack from "../../hooks/useSnack";

const FORM_LABELS = {
  entityName: "Role",
  nameLabel: "Role Name",
  keyLabel: "Role Key",
  keyHelper:
    "Stable key — auto-normalized to lowercase + underscores. Cannot be changed later.",
  errName: "Role name is required",
  errKey: "Role key is required",
};

const RolesCatalogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editEntity, setEditEntity] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const { snack, showSnack, closeSnack } = useSnack();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listRoles(statusFilter === "" ? null : statusFilter);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading roles:", err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async ({ name, key, description }) => {
    setSaving(true);
    try {
      const res = editEntity
        ? await updateRole({
            roleId: editEntity.roleId,
            roleName: name,
            description,
          })
        : await createRole({
            roleName: name,
            roleKey: key,
            description,
          });
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          editEntity ? "Role updated successfully" : "Role created successfully"
        );
        setFormOpen(false);
        setEditEntity(null);
        loadData();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error saving role", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    const r = confirmAction.role;
    setSaving(true);
    try {
      const res = await deactivateRole(r.roleId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        const summary = `${res.usersAffected ?? 0} user(s) lost this role and ${res.permissionsRemoved ?? 0} permission(s) were removed.`;
        showSnack(`Role deactivated successfully — ${summary}`);
        loadData();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error", "error");
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const handleReactivate = async () => {
    const r = confirmAction.role;
    setSaving(true);
    try {
      const res = await reactivateRole(r.roleId);
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack("Role reactivated successfully");
        loadData();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || "Error", "error");
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const columns = [
    {
      field: "roleName",
      headerName: "Role Name",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {row.isSystem && (
            <Tooltip title="System Role">
              <VerifiedUserIcon sx={{ fontSize: 16, color: "warning.main" }} />
            </Tooltip>
          )}
          <Typography variant="body2" fontWeight={500}>
            {row.roleName}
          </Typography>
        </Box>
      ),
    },
    {
      field: "roleKey",
      headerName: "Role Key",
      width: 160,
      renderCell: ({ value }) => (
        <Typography variant="caption" fontFamily="monospace">
          {value}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap title={value}>
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "usersCount",
      headerName: "Users",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip label={value ?? 0} size="small" variant="outlined" />
      ),
    },
    {
      field: "permissionsCount",
      headerName: "Permissions",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip label={value ?? 0} size="small" variant="outlined" />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: ({ value }) => <StatusChip active={value === 1} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => {
                setEditEntity({
                  roleId: row.roleId,
                  name: row.roleName,
                  key: row.roleKey,
                  description: row.description,
                });
                setFormOpen(true);
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status === 1 ? (
            <Tooltip
              title={
                row.isSystem
                  ? "System roles cannot be deactivated"
                  : "Deactivate"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={row.isSystem}
                  onClick={() =>
                    setConfirmAction({ type: "deactivate", role: row })
                  }
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Reactivate">
              <IconButton
                size="small"
                color="success"
                onClick={() =>
                  setConfirmAction({ type: "reactivate", role: row })
                }
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditEntity(null);
            setFormOpen(true);
          }}
        >
          New Role
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.roleId}
        loading={loading}
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
        }}
        disableRowSelectionOnClick
        density="comfortable"
        sx={{ border: "none" }}
      />

      <CatalogFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditEntity(null);
        }}
        onSave={handleSave}
        entity={editEntity}
        labels={FORM_LABELS}
        loading={saving}
      />

      <ConfirmDialog
        open={confirmAction?.type === "deactivate"}
        title="Deactivate Role"
        message={`Deactivate role "${confirmAction?.role?.roleName}"? This will revoke this role from all users and remove all its permissions. Reversible, but reactivation does NOT restore users or permissions.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      <ConfirmDialog
        open={confirmAction?.type === "reactivate"}
        title="Reactivate Role"
        message={`Reactivate role "${confirmAction?.role?.roleName}"? Users and permissions will NOT be reassigned automatically — you must add them manually.`}
        confirmLabel="Reactivate"
        confirmColor="success"
        onConfirm={handleReactivate}
        onCancel={() => setConfirmAction(null)}
        loading={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesCatalogTab;
